import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Progress } from '@/components/ui/progress';
import { ChevronRight, ChevronLeft, Heart, Activity, User, Target } from 'lucide-react';
import { toast } from 'react-hot-toast';
import userService from '../services/userService';

const HealthOnboarding = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    // Personal Information
    age: '',
    gender: '',
    height: '',
    weight: '',
    bloodType: '',
    
    // Medical History
    chronicConditions: [],
    allergies: '',
    currentMedications: '',
    previousSurgeries: '',
    familyHistory: '',
    
    // Lifestyle Factors
    activityLevel: '',
    smokingStatus: '',
    alcoholConsumption: '',
    sleepHours: '',
    dietaryPreferences: '',
    stressLevel: '',
    
    // Health Goals
    primaryHealthGoals: [],
    specificConcerns: '',
    fitnessGoals: '',
    
    // Emergency Contact
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelation: '',
    
    // Doctor Information
    primaryDoctorName: '',
    primaryDoctorPhone: '',
    preferredHospital: ''
  });

  const commonConditions = [
    'Diabetes', 'Hypertension', 'Heart Disease', 'Asthma', 'Arthritis',
    'Depression', 'Anxiety', 'Obesity', 'High Cholesterol', 'Thyroid Issues',
    'Migraines', 'GERD', 'Sleep Apnea', 'Osteoporosis', 'Other'
  ];

  const healthGoals = [
    'Weight Management', 'Blood Pressure Control', 'Blood Sugar Management',
    'Cholesterol Improvement', 'Fitness Enhancement', 'Stress Reduction',
    'Better Sleep', 'Nutrition Improvement', 'Pain Management', 'Mental Health',
    'Preventive Care', 'Energy Boost'
  ];

  const steps = [
    {
      title: 'Personal Information',
      icon: User,
      description: 'Basic physical information'
    },
    {
      title: 'Medical History',
      icon: Heart,
      description: 'Health conditions and medical background'
    },
    {
      title: 'Lifestyle',
      icon: Activity,
      description: 'Daily habits and lifestyle factors'
    },
    {
      title: 'Health Goals',
      icon: Target,
      description: 'Your health objectives and concerns'
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter(item => item !== value)
    }));
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in to continue');
        window.location.href = '/login';
        return;
      }
      
      await userService.updateHealthProfile(formData);
      toast.success('Health profile created successfully!');
      onComplete();
    } catch (error) {
      console.error('Error saving health profile:', error);
      
      if (error.message?.includes('Not authorized') || error.message?.includes('401')) {
        toast.error('Session expired. Please log in again.');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else if (error.message?.includes('400')) {
        toast.error('Please check your information and try again.');
      } else {
        toast.error('Unable to save health profile. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderPersonalInfo = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="age">Age</Label>
          <Input
            id="age"
            type="number"
            placeholder="Enter your age"
            value={formData.age}
            onChange={(e) => handleInputChange('age', e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="gender">Gender</Label>
          <Select value={formData.gender} onValueChange={(value) => handleInputChange('gender', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select gender" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
              <SelectItem value="other">Other</SelectItem>
              <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="height">Height (cm)</Label>
          <Input
            id="height"
            type="number"
            placeholder="Enter height in cm"
            value={formData.height}
            onChange={(e) => handleInputChange('height', e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="weight">Weight (kg)</Label>
          <Input
            id="weight"
            type="number"
            placeholder="Enter weight in kg"
            value={formData.weight}
            onChange={(e) => handleInputChange('weight', e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="bloodType">Blood Type</Label>
        <Select value={formData.bloodType} onValueChange={(value) => handleInputChange('bloodType', value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select blood type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="A+">A+</SelectItem>
            <SelectItem value="A-">A-</SelectItem>
            <SelectItem value="B+">B+</SelectItem>
            <SelectItem value="B-">B-</SelectItem>
            <SelectItem value="AB+">AB+</SelectItem>
            <SelectItem value="AB-">AB-</SelectItem>
            <SelectItem value="O+">O+</SelectItem>
            <SelectItem value="O-">O-</SelectItem>
            <SelectItem value="unknown">Unknown</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  const renderMedicalHistory = () => (
    <div className="space-y-6">
      <div>
        <Label>Chronic Conditions (Select all that apply)</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
          {commonConditions.map((condition) => (
            <div key={condition} className="flex items-center space-x-2">
              <Checkbox
                id={condition}
                checked={formData.chronicConditions.includes(condition)}
                onCheckedChange={(checked) => handleArrayChange('chronicConditions', condition, checked)}
              />
              <Label htmlFor={condition} className="text-sm">{condition}</Label>
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label htmlFor="allergies">Allergies</Label>
        <Textarea
          id="allergies"
          placeholder="List any allergies (food, medication, environmental)"
          value={formData.allergies}
          onChange={(e) => handleInputChange('allergies', e.target.value)}
        />
      </div>

      <div>
        <Label htmlFor="currentMedications">Current Medications</Label>
        <Textarea
          id="currentMedications"
          placeholder="List all current medications with dosages"
          value={formData.currentMedications}
          onChange={(e) => handleInputChange('currentMedications', e.target.value)}
        />
      </div>

      <div>
        <Label htmlFor="previousSurgeries">Previous Surgeries</Label>
        <Textarea
          id="previousSurgeries"
          placeholder="List any previous surgeries with dates"
          value={formData.previousSurgeries}
          onChange={(e) => handleInputChange('previousSurgeries', e.target.value)}
        />
      </div>

      <div>
        <Label htmlFor="familyHistory">Family Medical History</Label>
        <Textarea
          id="familyHistory"
          placeholder="Relevant family medical history (heart disease, diabetes, cancer, etc.)"
          value={formData.familyHistory}
          onChange={(e) => handleInputChange('familyHistory', e.target.value)}
        />
      </div>
    </div>
  );

  const renderLifestyle = () => (
    <div className="space-y-6">
      <div>
        <Label>Activity Level</Label>
        <RadioGroup
          value={formData.activityLevel}
          onValueChange={(value) => handleInputChange('activityLevel', value)}
          className="mt-2"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="sedentary" id="sedentary" />
            <Label htmlFor="sedentary">Sedentary (little to no exercise)</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="light" id="light" />
            <Label htmlFor="light">Light (1-3 days/week)</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="moderate" id="moderate" />
            <Label htmlFor="moderate">Moderate (3-5 days/week)</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="active" id="active" />
            <Label htmlFor="active">Active (6-7 days/week)</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="very-active" id="very-active" />
            <Label htmlFor="very-active">Very Active (2x/day or intense training)</Label>
          </div>
        </RadioGroup>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="smokingStatus">Smoking Status</Label>
          <Select value={formData.smokingStatus} onValueChange={(value) => handleInputChange('smokingStatus', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select smoking status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="never">Never smoked</SelectItem>
              <SelectItem value="former">Former smoker</SelectItem>
              <SelectItem value="current">Current smoker</SelectItem>
              <SelectItem value="occasional">Occasional smoker</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="alcoholConsumption">Alcohol Consumption</Label>
          <Select value={formData.alcoholConsumption} onValueChange={(value) => handleInputChange('alcoholConsumption', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select alcohol consumption" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="occasional">Occasional (1-2 drinks/week)</SelectItem>
              <SelectItem value="moderate">Moderate (3-7 drinks/week)</SelectItem>
              <SelectItem value="heavy">Heavy (8+ drinks/week)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="sleepHours">Average Sleep Hours</Label>
          <Input
            id="sleepHours"
            type="number"
            placeholder="Hours per night"
            value={formData.sleepHours}
            onChange={(e) => handleInputChange('sleepHours', e.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="stressLevel">Stress Level (1-10)</Label>
          <Input
            id="stressLevel"
            type="number"
            min="1"
            max="10"
            placeholder="Rate your stress level"
            value={formData.stressLevel}
            onChange={(e) => handleInputChange('stressLevel', e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="dietaryPreferences">Dietary Preferences/Restrictions</Label>
        <Textarea
          id="dietaryPreferences"
          placeholder="Vegetarian, vegan, keto, allergies, etc."
          value={formData.dietaryPreferences}
          onChange={(e) => handleInputChange('dietaryPreferences', e.target.value)}
        />
      </div>
    </div>
  );

  const renderHealthGoals = () => (
    <div className="space-y-6">
      <div>
        <Label>Primary Health Goals (Select all that apply)</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
          {healthGoals.map((goal) => (
            <div key={goal} className="flex items-center space-x-2">
              <Checkbox
                id={goal}
                checked={formData.primaryHealthGoals.includes(goal)}
                onCheckedChange={(checked) => handleArrayChange('primaryHealthGoals', goal, checked)}
              />
              <Label htmlFor={goal} className="text-sm">{goal}</Label>
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label htmlFor="specificConcerns">Specific Health Concerns</Label>
        <Textarea
          id="specificConcerns"
          placeholder="Any specific health concerns or symptoms you'd like to monitor"
          value={formData.specificConcerns}
          onChange={(e) => handleInputChange('specificConcerns', e.target.value)}
        />
      </div>

      <div>
        <Label htmlFor="fitnessGoals">Fitness Goals</Label>
        <Textarea
          id="fitnessGoals"
          placeholder="Specific fitness or wellness goals you want to achieve"
          value={formData.fitnessGoals}
          onChange={(e) => handleInputChange('fitnessGoals', e.target.value)}
        />
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold mb-4">Emergency Contact</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="emergencyContactName">Contact Name</Label>
            <Input
              id="emergencyContactName"
              placeholder="Emergency contact name"
              value={formData.emergencyContactName}
              onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="emergencyContactPhone">Contact Phone</Label>
            <Input
              id="emergencyContactPhone"
              placeholder="Emergency contact phone"
              value={formData.emergencyContactPhone}
              onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
            />
          </div>
        </div>
        <div className="mt-4">
          <Label htmlFor="emergencyContactRelation">Relationship</Label>
          <Input
            id="emergencyContactRelation"
            placeholder="Relationship to emergency contact"
            value={formData.emergencyContactRelation}
            onChange={(e) => handleInputChange('emergencyContactRelation', e.target.value)}
          />
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold mb-4">Healthcare Provider</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="primaryDoctorName">Primary Doctor Name</Label>
            <Input
              id="primaryDoctorName"
              placeholder="Your primary doctor's name"
              value={formData.primaryDoctorName}
              onChange={(e) => handleInputChange('primaryDoctorName', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="primaryDoctorPhone">Doctor's Phone</Label>
            <Input
              id="primaryDoctorPhone"
              placeholder="Doctor's phone number"
              value={formData.primaryDoctorPhone}
              onChange={(e) => handleInputChange('primaryDoctorPhone', e.target.value)}
            />
          </div>
        </div>
        <div className="mt-4">
          <Label htmlFor="preferredHospital">Preferred Hospital</Label>
          <Input
            id="preferredHospital"
            placeholder="Your preferred hospital or medical center"
            value={formData.preferredHospital}
            onChange={(e) => handleInputChange('preferredHospital', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return renderPersonalInfo();
      case 1:
        return renderMedicalHistory();
      case 2:
        return renderLifestyle();
      case 3:
        return renderHealthGoals();
      default:
        return null;
    }
  };

  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="w-6 h-6 text-red-500" />
            Health Profile Setup
          </CardTitle>
          <CardDescription>
            Help us personalize your health experience by providing some basic information
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">
                Step {currentStep + 1} of {steps.length}
              </span>
              <span className="text-sm text-gray-500">{Math.round(progress)}% Complete</span>
            </div>
            <Progress value={progress} className="w-full" />
          </div>

          {/* Step Navigation */}
          <div className="flex justify-between items-center mb-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div
                  key={index}
                  className={`flex flex-col items-center p-2 rounded-lg ${
                    index === currentStep
                      ? 'bg-blue-50 text-blue-600'
                      : index < currentStep
                      ? 'bg-green-50 text-green-600'
                      : 'text-gray-400'
                  }`}
                >
                  <Icon className="w-6 h-6 mb-1" />
                  <span className="text-xs font-medium hidden md:block">{step.title}</span>
                </div>
              );
            })}
          </div>

          {/* Current Step Content */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-2">{steps[currentStep].title}</h2>
            <p className="text-gray-600 mb-6">{steps[currentStep].description}</p>
            {renderCurrentStep()}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 0}
              className="flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>

            {currentStep < steps.length - 1 ? (
              <Button onClick={nextStep} className="flex items-center gap-2">
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                {isLoading ? 'Saving...' : 'Complete Setup'}
                <Heart className="w-4 h-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HealthOnboarding;